import { useState, useCallback } from "react";

const useQuantityChangeConfirmation = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [totals, setTotals] = useState({
    originalGrandTotal: 0,
    newGrandTotal: 0,
    difference: 0,
  });
  const [onConfirmCallback, setOnConfirmCallback] = useState(null);

  const checkForQuantityChanges = useCallback((items) => {
    const changes = [];
    let originalGrandTotal = 0;
    let newGrandTotal = 0;
    
    if (!items || items.length === 0) {
      return { changes: [], totals: { originalGrandTotal: 0, newGrandTotal: 0, difference: 0 } };
    }
    
    items.forEach((item, index) => {
      // Get the original quantity from when the item was loaded
      const originalQty = item.originalQtyOnLoad ?? item.original_qty ?? item.qty;
      const currentQty = item.qty;
      
      // Check if quantity actually changed
      const hasChanged = originalQty !== currentQty;
      
      if (hasChanged) {
        // Get original unit price (before any changes)
        const originalUnitPrice = item.original_unit_price_on_load || item.original_unit_price || item.unit_price;
        const currentUnitPrice = item.unit_price;
        
        // Calculate prices
        const originalTotalPrice = originalQty * originalUnitPrice;
        const currentTotalPrice = item.total_price || (currentQty * currentUnitPrice);
        
        changes.push({
          index,
          itemId: item.id,
          itemName: item.price?.item?.name || item.name || item.style || `Item ${index + 1}`,
          originalQty: originalQty,
          newQty: currentQty,
          originalUnitPrice: originalUnitPrice,
          newUnitPrice: currentUnitPrice,
          originalTotalPrice: originalTotalPrice,
          newTotalPrice: currentTotalPrice,
          estimatedShipping: item.estimated_shipping_charges || 0,
          originalData: {
            qty: originalQty,
            unit_price: originalUnitPrice,
            original_unit_price: originalUnitPrice,
            total_price: originalTotalPrice,
            original_total_price: originalTotalPrice,
            sub_total: item.original_total_price_on_load || originalTotalPrice + (item.estimated_shipping_charges || 0) + (item.extra_charges || 0) + (item.artwork_charges || 0),
            grand_total: item.grand_total,
            gst_amount: item.gst_amount,
            estimated_shipping_charges: item.estimated_shipping_charges,
            extra_charges: item.extra_charges,
            artwork_charges: item.artwork_charges,
          },
        });
        
        originalGrandTotal += originalTotalPrice;
        newGrandTotal += currentTotalPrice;
      } else {
        // Item unchanged, add current total to both
        const itemTotal = item.total_price || 0;
        originalGrandTotal += itemTotal;
        newGrandTotal += itemTotal;
      }
    });
    
    return {
      changes,
      totals: {
        originalGrandTotal,
        newGrandTotal,
        difference: newGrandTotal - originalGrandTotal,
      },
    };
  }, []);

  const requestConfirmation = useCallback((items, onConfirm) => {
    const { changes, totals: calculatedTotals } = checkForQuantityChanges(items);
    
    if (changes.length > 0) {
      setPendingChanges(changes);
      setTotals(calculatedTotals);
      setOnConfirmCallback(() => onConfirm);
      setShowConfirmDialog(true);
      return true; // Confirmation needed
    }
    
    return false; // No confirmation needed
  }, [checkForQuantityChanges]);

  const handleConfirm = useCallback(() => {
    setShowConfirmDialog(false);
    if (onConfirmCallback) {
      onConfirmCallback(pendingChanges);
    }
    setPendingChanges([]);
    setTotals({ originalGrandTotal: 0, newGrandTotal: 0, difference: 0 });
    setOnConfirmCallback(null);
  }, [onConfirmCallback, pendingChanges]);

  const handleCancel = useCallback(() => {
    setShowConfirmDialog(false);
    setPendingChanges([]);
    setTotals({ originalGrandTotal: 0, newGrandTotal: 0, difference: 0 });
    setOnConfirmCallback(null);
  }, []);

  const prepareItemsWithConfirmation = useCallback((items, confirmedChanges) => {
    return items.map((item, index) => {
      const change = confirmedChanges.find((c) => c.index === index);
      if (change) {
        return {
          ...item,
          qty_change_confirmed: true,
          original_order_data: change.originalData,
          original_qty: change.originalQty,
        };
      }
      return item;
    });
  }, []);

  return {
    showConfirmDialog,
    pendingChanges,
    totals,
    requestConfirmation,
    handleConfirm,
    handleCancel,
    prepareItemsWithConfirmation,
  };
};

export default useQuantityChangeConfirmation;
